export type OnlineTransport = 'browser' | 'relay';

type OnlineResolver = (trackId: string, quality: string, signal?: AbortSignal) => Promise<Record<string, any>>;
type OnlineTransportOptions = {
  trackId: string;
  quality: string;
  signal?: AbortSignal;
  transport: OnlineTransport;
  browser: OnlineResolver;
  relay: OnlineResolver;
};

// Native Audio can play a cross-origin resource even when fetch cannot read
// its bytes. Only an actual media failure warrants a relay; autoplay denial
// and cancellation cannot be fixed by changing transport.
export function shouldRelayNativePlayback(resolved: { trackId?: string; transport?: string; nativeCandidate?: boolean } | null, trackId: string, error: unknown, mediaErrorCode?: number) {
  const name = String((error as { name?: string })?.name || '');
  return resolved?.trackId === trackId && resolved.transport === 'browser' && resolved.nativeCandidate === true &&
    !['AbortError', 'NotAllowedError'].includes(name) && (name === 'NotSupportedError' || [2, 3, 4].includes(mediaErrorCode || 0));
}

// A browser resolver can be healthy while its API or CDN is unavailable to
// browser CORS. Keep the selected preference untouched and retry the exact
// recording and quality through the server relay only for these transport
// failures. Unsupported URLs, identity errors and access errors must stop.
const RELAY_FALLBACK_CODES = new Set(['SOURCE_BROWSER_NETWORK', 'SOURCE_CORS_UNAVAILABLE', 'SOURCE_TIMEOUT']);

const codeOf = (error: unknown) => String((error as { code?: string })?.code || '');
const safeDiagnostics = (error: unknown) => {
  const diagnostics = (error as { diagnostics?: unknown })?.diagnostics;
  return diagnostics && typeof diagnostics === 'object' ? diagnostics as Record<string, unknown> : undefined;
};

function relayFailure(browserError: unknown, relayError: unknown): never {
  const value = relayError instanceof Error ? relayError : new Error(String(relayError));
  const browserDiagnostics = safeDiagnostics(browserError);
  const relayDiagnostics = safeDiagnostics(relayError);
  // Keep the relay code/stage as the actionable final failure while retaining
  // the original browser code/stage for diagnosis of the automatic fallback.
  const diagnostics = { ...(relayDiagnostics || {}) } as Record<string, unknown>;
  diagnostics.transportFallback = {
    from: 'browser',
    code: codeOf(browserError),
    ...(browserDiagnostics ? { diagnostics: browserDiagnostics } : {}),
  };
  Object.assign(value, { diagnostics });
  throw value;
}

export async function resolveOnlineWithFallback({ trackId, quality, signal, transport, browser, relay }: OnlineTransportOptions) {
  if (transport === 'relay') return { ...(await relay(trackId, quality, signal)), transport: 'relay' as const };
  try {
    return { ...(await browser(trackId, quality, signal)), transport: 'browser' as const };
  } catch (browserError) {
    if (signal?.aborted || !RELAY_FALLBACK_CODES.has(codeOf(browserError))) throw browserError;
    try {
      return { ...(await relay(trackId, quality, signal)), transport: 'relay' as const };
    } catch (relayError) {
      relayFailure(browserError, relayError);
    }
  }
}
